import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Button } from 'react-bootstrap';

import styles from './CustomResponseFields.module.scss'
import ErrorCatcher from '../../common/ErrorCatcher';

import Events from '../../../api/Events';

/**
 * 
 * @param {int} eventId - required, defines the event to get fields for
 * @param {int} userId  - optional - will change this to an editing state, which is handled here
 * @param {bool} submit - if true, will render this as a form itself with a submit button
 * @param {func} submissionFunction  - If submit is true, this function can pass things back up to the parent
 * @param {func} validationFunction - Will pass validation and any errors for validation back up to the parent as {bool, array}
 * @param {array} importedCustomFields - If you want to pass in your own custom fields, this will use those instead of fetching them
 * @param {array} importedAnswers - If you want to pass in your own answers, this will use those instead of fetching them
 */
export const CustomResponseFields = ({
    eventId,
    userId,
    submit=false,
    submissionFunction=null,
    validationFunction=null,
    importedCustomFields=null,
    importedAnswers=null,
    ...props
})=>{

    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState();
    const [customFields, setCustomFields] = useState([]);
    const [firstLoad, setFirstLoad] = useState(true);
    
//#region callbacks/effects
    const handleFieldValidation = useCallback(()=>{
        const formElements = document.querySelectorAll('[data-field-id]')
        let validated = true;
        let errors = [];
        formElements.forEach(element=>{
            if(element.required && !element?.value){
                validated = false;
                if(errors?.length === 0) errors.push('Data missing in required fields.  Please fill out the following:')
                errors.push(`"${element.placeholder}".`)
            }
        })
        if(validationFunction) validationFunction(validated, errors.length > 0 ? errors : null);
    },[validationFunction])
    
    const selectFieldTemplate = useCallback((item, i, value="")=>{

        const fieldValue = document.querySelector(`[name="custom_${item?.name}"]`)?.value || "";

        return(
            <p 
                className={styles["custom-select"]}
                key={`custom-select-${item?.name}-${i}`}
            >
                <label data-cy={`custom-response-select-label-${item?.name}`} >
                    {item?.placeholder_text} {item?.required ? <span className="required-star">*</span> : null}
                </label>
                <select
                    className={`${item?.required && !fieldValue ? styles["required"] : ""}`}
                    key={`custom-select-${item?.name}-${i}`}
                    name={`custom_${item?.name}`}
                    data-cy={`custom-response-select-${item?.name}`}
                    data-field-id={item?.id}
                    placeholder={item?.placeholder_text}
                    required={item?.required}
                    defaultValue={value || item?.default_value}
                    onChange={handleFieldValidation}
                >
                    <option
                        value=""
                        disabled={item?.required ? true : false}
                    >
                        Select an Answer
                    </option>
                    {item.options.map((option, j) => (
                        <option
                            key={`custom-select-${item.id}-${j}`}
                            value={option.value}
                        >
                            {option.text}
                        </option>
                    ))}
                </select>

            </p>
        )
    },[handleFieldValidation])

    const inputFieldTemplate = useCallback((item, i, value)=>{
        const fieldValue = document.querySelector(`[name="custom_${item?.name}"]`)?.value || "";

        return(
            <p 
                className={styles["custom-input"]}
                key={`custom-input-${item?.name}-${i}`}
            >
                <label data-cy={`custom-response-input-label-${item?.name}`}>
                    {item?.placeholder_text} {item?.required && !fieldValue ? <span className="required-star">*</span> : null}
                </label>
                <input
                    className={`${item?.required ? styles["required"] : ""}`}
                    type="text"
                    required={item?.required}
                    name={`custom_${item?.name}`}
                    data-cy={`custom-response-input-${item?.name}`}
                    data-field-id={item?.id}
                    placeholder={item?.placeholder_text}
                    defaultValue={value || item?.default_value}
                    onChange={handleFieldValidation}
                />
            </p>
        )
    },[handleFieldValidation])

    const assignFields = useCallback((fields)=>{
        return fields.map((item, i)=>{
            if(item.custom_field_type==="select") return selectFieldTemplate(item, i, item.value);
            else return inputFieldTemplate(item, i, item.value);
        })
    },[inputFieldTemplate, selectFieldTemplate])

    const getCustomFields = useCallback(async(id)=>{
        try{
            let response = await Events.get_custom_fields({event_id: id});
            if(response.status===200 &&  response.data){
                let customFieldElements = assignFields(response.data)
                setCustomFields(customFieldElements)
                if(response?.data?.length === 0 && validationFunction) validationFunction(true, null) 
            }
        //202 was added to the endpoint when there's no data - have to account for that, not just a 200 with no data
        else if(response.status===202 && validationFunction) validationFunction(true, null)
        }catch(ex){
            console.error(ex)
            setErrors(ex)
        }
    },[assignFields]);

    const getUserResponses=useCallback(async(event, user)=>{
        try{
            let response = await Events.get_user_responses({event_id: event, user_id: user});
            if(response.status===200 &&  response.data){
                let responseTwo = await Events.get_custom_fields({event_id: event});
                let userAnswers = response.data[0].custom_responses;
                if(responseTwo.status===200 &&  responseTwo.data){
                    responseTwo?.data?.forEach((item)=>{item.value = userAnswers?.find((answer)=>answer.id === item.id)?.value || ""})
                    let customFieldElements = assignFields(responseTwo?.data)
                    setCustomFields(customFieldElements)
                }
            }else setErrors("There was a problem getting the user's responses")
        }catch(ex){
            console.error(ex)
            setErrors(ex)
        }
    },[assignFields])

    useEffect(()=>{
        if(userId && eventId && !importedCustomFields) getUserResponses(eventId, userId);
        else if(eventId && !importedCustomFields) getCustomFields(eventId);
        else if(importedAnswers && importedCustomFields){
            importedCustomFields.forEach((item)=>{item.value = importedAnswers?.find((answer)=>answer.id === item.id)?.value || ""})
            setCustomFields(assignFields(importedCustomFields))
        } 
    },[eventId, userId, getCustomFields, getUserResponses, assignFields, importedAnswers, importedCustomFields]);

    useEffect(()=>{
        //we need to make sure we're validating at least once, always.  
        //Otherwise the parent component will never be validated
        if(firstLoad &&customFields.length > 0) {
            handleFieldValidation();
            setFirstLoad(false);
        }
    },[handleFieldValidation])
//#endregion callbacks/effects


//#region functions/handlers
    
    const validateRequired=(formElements, formData)=>{
        let validated = true;
        let errors = [];
        formElements.forEach(item=>{
            if(item?.required && !formData.get(item.name)){
                validated = false;
                if(errors?.length === 0) errors.push('Data missing in required fields.  Please fill out the following:')
                errors.push(`${item?.placeholder}.`)
            }
        });
        if(!validated) setErrors(errors);
        return validated;
    }

    const submitHandler = async(e)=>{
        setLoading(true);
        e.preventDefault();
        e.stopPropagation();

        const formData = new FormData(e.target);
        const formElements = e.target.querySelectorAll('[data-field-id]');

        const validated = validateRequired(formElements, formData);
        
        if(userId && validated){
            editResponses(formData, formElements)
        }
    }

    /** To edit responses for a user that already exists, as it's a one and done deal */
    const editResponses = async(formData, formElements)=>{
        const responseArray = []
        formElements.forEach(element => {
            let responseObj = {}
            responseObj.custom_field_id = element.getAttribute('data-field-id');
            responseObj.fieldName = element.name
            responseObj.value = formData.get(element.name);
            responseArray.push(responseObj)
        });
        try{
            let response = await Events.editUserResponses({
                event_id: eventId,
                user_id: userId,
                responses: responseArray
            });
            if(response.status===200 &&  response.data){
                if(submissionFunction) submissionFunction();
                //refresh data
            }
        }catch(ex){
            console.error(ex)
        }
        setLoading(false)
    }

//#endregion functions/handlers

    return(
        <div 
            style={{maxWidth: "250px"}}
            className={styles["custom-field-wrapper"]}
        >
            {loading ?
                <div data-cy="custom-response-fields-loading">Loading...</div>
                :
                <>
                    {submit ? 
                        <form onSubmit={submitHandler} data-cy="custom-response-fields-form">
                            {customFields}
                            <Button type="submit">
                                {userId ? "Update" : "Submit"}
                            </Button>
                            
                            
                        </form>
                        :
                        <>
                            {customFields}
                        </>
                    }
                </>
            }
            {errors && <ErrorCatcher error={errors} onClose={()=>setErrors("")} data-cy="custom-response-fields-error-catcher" />}
        </div>
    )
}